import { BaseApplicationService } from "../../../domain/base/base-application-service";
import apiClient from "../../../apis/tenant-management/tenant-management-client";
import { MFAUiState, MFAUiStateMatcher } from "./mfa-ui-state";
import { toast } from "react-toastify";
import { CloudGovApiError } from "../../../domain/api-error";
import {
  reportAsyncError,
  reportUnexpectedError,
} from "../../../../error-reporter";

export type MFAServiceProps = {
  handleSwitch: () => void;
  fetchMFAStatus: () => void;
  isMFAEnabled: boolean;
  isLoading: boolean;
  curStep: MFAUiState;
  matchUiState: typeof MFAService.prototype.matchUiState;
  checkPassword: (password: string) => void;
  initTOTP: typeof MFAService.prototype.initTOTP;
  enableTOTP: (TOTP: string) => void;
  disableTOTP: () => void;
  cancel: () => void;
};

export class MFAService extends BaseApplicationService<MFAServiceProps> {
  state = {
    handleSwitch: this.handleSwitch.bind(this),
    fetchMFAStatus: this.fetchMFAStatus.bind(this),
    isMFAEnabled: false,
    isLoading: true,
    curStep: MFAUiState.initialize(),
    matchUiState: this.matchUiState.bind(this),
    checkPassword: this.checkPassword.bind(this),
    initTOTP: this.initTOTP.bind(this),
    enableTOTP: this.enableTOTP.bind(this),
    disableTOTP: this.disableTOTP.bind(this),
    cancel: this.cancel.bind(this),
  };

  async fetchMFAStatus() {
    try {
      const { data: status } = await apiClient.fetchMFAStatus();
      this.setState({
        isMFAEnabled: !!status,
        isLoading: false,
      });
    } catch (e) {
      if (CloudGovApiError.isRetryable(e)) {
        toast.error(
          "通信に失敗しました。ネットワーク接続などをご確認の上、再実行してください。",
        );
      }
      reportUnexpectedError(e);
    }
  }

  handleSwitch() {
    this.setState({
      curStep: this.state.curStep.nextMFAUi(this.state.isMFAEnabled),
    });
  }

  matchUiState<R>(matcher: MFAUiStateMatcher<R>): R {
    return this.state.curStep.matchType(matcher);
  }

  async checkPassword(password: string) {
    try {
      const {
        data: { isAuthed },
      } = await apiClient.checkOrgUserPassword(password);
      if (isAuthed) {
        this.setState({ curStep: this.state.curStep.nextMFAUi() });
        return;
      }
      // isAuth must be true when status code is 200, so should not reach here
      throw new TypeError("unexpected response from check password api");
    } catch (e) {
      if (CloudGovApiError.isRetryable(e)) {
        toast.error(
          "通信に失敗しました。ネットワーク接続などをご確認の上、再実行してください。",
        );
        return;
      }
      if (CloudGovApiError.isErrorCode(e, "bad_request")) {
        toast.error("パスワードが間違っています。");
        return;
      }
      reportAsyncError(e);
    }
  }

  async initTOTP() {
    try {
      const { data } = await apiClient.initTOTP();
      return data as { qr: Uint8Array; key: string };
    } catch (e) {
      if (CloudGovApiError.isRetryable(e)) {
        toast.error(
          "通信に失敗しました。ネットワーク接続などをご確認の上、再実行してください。",
        );
      }
      reportAsyncError(e);
    }
    return null;
  }

  async enableTOTP(TOTP: string) {
    try {
      await apiClient.enableTOTP(TOTP);
      toast.success("2段階認証設定が有効になりました。");
      this.setState({
        isMFAEnabled: true,
        curStep: this.state.curStep.nextMFAUi(),
      });
    } catch (e) {
      if (CloudGovApiError.isRetryable(e)) {
        toast.error(
          "通信に失敗しました。ネットワーク接続などをご確認の上、再実行してください。",
        );
        return;
      }
      if (CloudGovApiError.isErrorCode(e, "bad_request")) {
        toast.error("認証コードが間違っています。");
        return;
      }
      reportAsyncError(e);
    }
  }

  async disableTOTP() {
    try {
      await apiClient.disableTOTP();
      toast.success("2段階認証設定が無効になりました。");
      this.setState({
        isMFAEnabled: false,
        curStep: this.state.curStep.nextMFAUi(),
      });
    } catch (e) {
      if (CloudGovApiError.isRetryable(e)) {
        toast.error(
          "通信に失敗しました。ネットワーク接続などをご確認の上、再実行してください。",
        );
        return;
      }
      reportAsyncError(e);
    }
  }

  cancel() {
    this.setState({ curStep: this.state.curStep.nextMFAUi() });
  }
}
